import React, { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { api } from "../../lib/api";
import { Card } from "../../components/ui/card";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import { Textarea } from "../../components/ui/textarea";
import { Badge } from "../../components/ui/badge";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "../../components/ui/select";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription,
} from "../../components/ui/dialog";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "../../components/ui/table";
import {
  Tabs, TabsContent, TabsList, TabsTrigger,
} from "../../components/ui/tabs";
import {
  ArrowLeft, Plus, Trash2, MapPin, Wand2, ExternalLink, Copy, CheckCircle2, XCircle,
  Loader2, Route, Search, History, Truck, Map as MapIcon, AlertTriangle,
} from "lucide-react";
import { toast } from "sonner";
import {
  TIPOS_ATENDIMENTO_PARADA, PRIORIDADES, STATUS_PARADA, STATUS_ROTA, badge,
  linkGoogleMaps, linkWaze, linkGoogleMapsRota,
} from "./_lib";

const PARADA_VAZIA = {
  cliente_admin_id: "",
  cliente_nome: "",
  cnpj_cpf: "",
  endereco: "",
  cidade: "",
  uf: "",
  telefone: "",
  tipo_atendimento: "entrega",
  equipamentos: [],
  prioridade: "media",
  observacoes: "",
  agrupar_endereco_duplicado: false,
};

export default function RotaDetalhe() {
  const { id } = useParams();
  const [rota, setRota] = useState(null);
  const [historico, setHistorico] = useState([]);
  const [loading, setLoading] = useState(true);
  const [openParada, setOpenParada] = useState(false);
  const [paradaForm, setParadaForm] = useState(PARADA_VAZIA);
  const [busy, setBusy] = useState(false);
  const [alertaDup, setAlertaDup] = useState(null);

  // Busca cliente
  const [clienteBusca, setClienteBusca] = useState("");
  const [clienteOpts, setClienteOpts] = useState([]);
  const [buscando, setBuscando] = useState(false);

  const carregar = async () => {
    setLoading(true);
    try {
      const [r, h] = await Promise.all([
        api.get(`/roteirizacao/rotas/${id}`),
        api.get(`/roteirizacao/rotas/${id}/historico`),
      ]);
      setRota(r.data); setHistorico(h.data?.items || []);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Rota não encontrada");
    } finally { setLoading(false); }
  };
  useEffect(() => { carregar(); /* eslint-disable-next-line */ }, [id]);

  // Busca de cliente (debounce 300ms)
  useEffect(() => {
    if (!clienteBusca || clienteBusca.length < 2) { setClienteOpts([]); return; }
    const t = setTimeout(async () => {
      setBuscando(true);
      try {
        const { data } = await api.get("/admin/clientes", { params: { q: clienteBusca, limit: 10 } });
        setClienteOpts(data?.items || []);
      } catch { setClienteOpts([]); }
      setBuscando(false);
    }, 300);
    return () => clearTimeout(t);
  }, [clienteBusca]);

  const selecionarCliente = (c) => {
    // Tenta puxar endereço do CRM (clientes_locativo só tem endereco/cidade/uf parciais)
    const endereco_parts = [c.endereco, c.cidade, c.uf].filter(Boolean).join(", ");
    setParadaForm({
      ...paradaForm,
      cliente_admin_id: c.id,
      cliente_nome: c.nome,
      cnpj_cpf: c.cnpj_cpf || "",
      endereco: endereco_parts || paradaForm.endereco,
      cidade: c.cidade || paradaForm.cidade,
      uf: c.uf || paradaForm.uf,
      telefone: c.telefone || paradaForm.telefone,
    });
    setClienteOpts([]);
    setClienteBusca("");
  };

  const abrirNovaParada = () => {
    setParadaForm(PARADA_VAZIA);
    setAlertaDup(null);
    setOpenParada(true);
  };

  const salvarParada = async (forcarAgrupar = false) => {
    if (!paradaForm.cliente_nome?.trim()) { toast.error("Cliente obrigatório"); return; }
    if (!paradaForm.endereco?.trim()) { toast.error("Endereço obrigatório"); return; }
    setBusy(true);
    try {
      const payload = { ...paradaForm, agrupar_endereco_duplicado: forcarAgrupar };
      const { data } = await api.post(`/roteirizacao/rotas/${id}/paradas`, payload);
      if (data?.alerta === "endereco_duplicado") {
        setAlertaDup({ msg: data.mensagem, cliente: data.cliente_existente });
        setBusy(false);
        return;
      }
      toast.success(forcarAgrupar ? "Parada agrupada" : "Parada adicionada");
      setOpenParada(false);
      setAlertaDup(null);
      carregar();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Falha");
    } finally { setBusy(false); }
  };

  const removerParada = async (pid) => {
    if (!window.confirm("Remover esta parada?")) return;
    try {
      await api.delete(`/roteirizacao/rotas/${id}/paradas/${pid}`);
      toast.success("Removida"); carregar();
    } catch (e) { toast.error(e?.response?.data?.detail || "Falha"); }
  };

  const otimizar = async () => {
    try {
      const { data } = await api.post(`/roteirizacao/rotas/${id}/otimizar`);
      toast.success(data?.msg || "Rota otimizada por cidade + prioridade");
      carregar();
    } catch (e) { toast.error(e?.response?.data?.detail || "Falha"); }
  };

  const concluir = async () => {
    if (!window.confirm("Concluir esta rota? Status passará para 'concluida'.")) return;
    try {
      await api.post(`/roteirizacao/rotas/${id}/concluir`);
      toast.success("Rota concluída"); carregar();
    } catch (e) { toast.error(e?.response?.data?.detail || "Falha"); }
  };

  const cancelar = async () => {
    if (!window.confirm("Cancelar esta rota?")) return;
    try {
      await api.post(`/roteirizacao/rotas/${id}/cancelar`);
      toast.success("Rota cancelada"); carregar();
    } catch (e) { toast.error(e?.response?.data?.detail || "Falha"); }
  };

  const atualizarStatusParada = async (pid, novoStatus) => {
    try {
      await api.patch(`/roteirizacao/rotas/${id}/paradas/${pid}`, { status: novoStatus });
      carregar();
    } catch (e) { toast.error(e?.response?.data?.detail || "Falha"); }
  };

  const copiarLinkMotorista = () => {
    const url = `${window.location.origin}/rota/${rota.link_token}`;
    navigator.clipboard?.writeText(url);
    toast.success("Link copiado — envie ao motorista");
  };

  if (loading || !rota) {
    return <div className="p-8 text-center"><Loader2 className="animate-spin inline" /></div>;
  }

  const bStatus = badge(rota.status, STATUS_ROTA);
  const encerrada = rota.status === "concluida" || rota.status === "cancelada";
  const linkRotaCompleta = linkGoogleMapsRota(rota.paradas || []);
  const linkPrimeiroWaze = (rota.paradas || [])[0]
    ? linkWaze(rota.paradas[0].endereco, rota.paradas[0].cidade, rota.paradas[0].uf)
    : null;

  // Resumo operacional
  const cidades = [...new Set((rota.paradas || []).map((p) => (p.cidade || "").toUpperCase()).filter(Boolean))];
  const ufs = [...new Set((rota.paradas || []).map((p) => (p.uf || "").toUpperCase()).filter(Boolean))];
  const altaPrio = (rota.paradas || []).filter((p) => p.prioridade === "alta").length;
  const concluidas = (rota.paradas || []).filter((p) => p.status === "concluido").length;

  return (
    <div className="space-y-4" data-testid="rota-detalhe-page">
      {/* Header */}
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <div className="flex items-center gap-3">
          <Link to="/app/roteirizacao"><Button variant="ghost" size="sm"><ArrowLeft size={14} /></Button></Link>
          <Route size={26} style={{ color: "#1B3A6B" }} />
          <div>
            <h1 className="text-2xl font-bold" style={{ color: "#1B3A6B" }}>Rota {rota.numero}</h1>
            <p className="text-xs text-muted-foreground">
              {rota.data} · {rota.motorista_nome || "(sem motorista)"} · {rota.veiculo_placa || "(sem veículo)"}
            </p>
          </div>
          <Badge className={bStatus.cls + " border"}>{bStatus.label}</Badge>
        </div>
        <div className="flex gap-2 flex-wrap">
          {!encerrada && (
            <>
              <Button
                onClick={otimizar}
                className="text-white shadow-md"
                style={{ background: "linear-gradient(135deg, #F97316, #ea580c)" }}
                data-testid="btn-otimizar"
              >
                <Wand2 size={14} className="mr-1" /> Gerar Melhor Rota
              </Button>
              <Button variant="outline" onClick={copiarLinkMotorista} data-testid="btn-copiar-link">
                <Copy size={14} className="mr-1" /> Link motorista
              </Button>
              <Button onClick={concluir} className="text-white" style={{ background: "#16a34a" }} data-testid="btn-concluir-rota">
                <CheckCircle2 size={14} className="mr-1" /> Concluir
              </Button>
              <Button variant="outline" onClick={cancelar} data-testid="btn-cancelar-rota">
                <XCircle size={14} className="mr-1" /> Cancelar
              </Button>
            </>
          )}
          {linkRotaCompleta && (
            <a href={linkRotaCompleta} target="_blank" rel="noopener noreferrer">
              <Button variant="outline" data-testid="btn-rota-mapa">
                <MapIcon size={14} className="mr-1" /> Ver rota completa no Maps
              </Button>
            </a>
          )}
          {linkPrimeiroWaze && (
            <a href={linkPrimeiroWaze} target="_blank" rel="noopener noreferrer">
              <Button variant="outline" className="bg-blue-50 text-blue-800" data-testid="btn-waze-prim">
                <ExternalLink size={14} className="mr-1" /> Abrir Waze (1ª parada)
              </Button>
            </a>
          )}
        </div>
      </div>

      {/* Resumo operacional */}
      {(rota.paradas || []).length > 0 && (
        <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-3" data-testid="resumo-operacional">
          <Card className="p-3 border-l-4 border-l-blue-500">
            <div className="text-[10px] text-muted-foreground">Paradas</div>
            <div className="text-2xl font-bold">{rota.paradas.length}</div>
          </Card>
          <Card className="p-3 border-l-4 border-l-emerald-500">
            <div className="text-[10px] text-muted-foreground">Concluídas</div>
            <div className="text-2xl font-bold text-emerald-700">{concluidas}/{rota.paradas.length}</div>
          </Card>
          <Card className="p-3 border-l-4 border-l-violet-500">
            <div className="text-[10px] text-muted-foreground">Cidades</div>
            <div className="text-2xl font-bold text-violet-700">{cidades.length}</div>
          </Card>
          <Card className="p-3 border-l-4 border-l-zinc-500">
            <div className="text-[10px] text-muted-foreground">UFs</div>
            <div className="text-2xl font-bold">{ufs.length}</div>
          </Card>
          <Card className="p-3 border-l-4 border-l-red-500">
            <div className="text-[10px] text-muted-foreground">Alta prioridade</div>
            <div className="text-2xl font-bold text-red-700">{altaPrio}</div>
          </Card>
          <Card className="p-3 border-l-4 border-l-amber-500 col-span-2 md:col-span-4 lg:col-span-1">
            <div className="text-[10px] text-muted-foreground">Cidades</div>
            <div className="text-[10px] leading-snug">{cidades.slice(0, 4).join(" · ") || "—"}{cidades.length > 4 ? " …" : ""}</div>
          </Card>
        </div>
      )}

      <Tabs defaultValue="paradas">
        <TabsList>
          <TabsTrigger value="paradas" data-testid="tab-paradas">
            <MapPin size={14} className="mr-1" /> Paradas ({rota.paradas?.length || 0})
          </TabsTrigger>
          <TabsTrigger value="info" data-testid="tab-info"><Truck size={14} className="mr-1" /> Info</TabsTrigger>
          <TabsTrigger value="historico" data-testid="tab-historico"><History size={14} className="mr-1" /> Histórico</TabsTrigger>
        </TabsList>

        {/* PARADAS */}
        <TabsContent value="paradas" className="pt-3">
          {!encerrada && (
            <div className="mb-3">
              <Button onClick={abrirNovaParada} className="text-white" style={{ background: "#F97316" }} data-testid="btn-add-parada">
                <Plus size={14} className="mr-1" /> Adicionar parada
              </Button>
            </div>
          )}
          <Card>
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>#</TableHead>
                  <TableHead>Cliente</TableHead>
                  <TableHead>Endereço</TableHead>
                  <TableHead>Tipo</TableHead>
                  <TableHead>Prioridade</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead className="text-right">Ações</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {(rota.paradas || []).length === 0 && (
                  <TableRow><TableCell colSpan={7} className="text-center py-6 text-muted-foreground">
                    Nenhuma parada. Use <strong>Adicionar parada</strong>.
                  </TableCell></TableRow>
                )}
                {(rota.paradas || []).map((p) => {
                  const bp = badge(p.status, STATUS_PARADA);
                  const bpr = badge(p.prioridade, PRIORIDADES);
                  return (
                    <TableRow key={p.id} data-testid={`parada-${p.id}`}>
                      <TableCell className="font-mono">{p.ordem}</TableCell>
                      <TableCell>
                        <div className="font-medium text-sm">{p.cliente_nome}</div>
                        <div className="text-[10px] text-muted-foreground">{p.cnpj_cpf || ""} {p.telefone ? `· ${p.telefone}` : ""}</div>
                      </TableCell>
                      <TableCell className="text-xs">
                        <div>{p.endereco}</div>
                        <div className="text-muted-foreground">{p.cidade}{p.uf ? `/${p.uf}` : ""}</div>
                        <div className="flex gap-1 mt-1">
                          <a href={linkGoogleMaps(p.endereco, p.cidade, p.uf)} target="_blank" rel="noopener noreferrer">
                            <Button size="sm" variant="ghost" className="h-6 px-2 text-[10px]" data-testid={`maps-${p.id}`}>
                              <ExternalLink size={10} className="mr-1" /> Maps
                            </Button>
                          </a>
                          <a href={linkWaze(p.endereco, p.cidade, p.uf)} target="_blank" rel="noopener noreferrer">
                            <Button size="sm" variant="ghost" className="h-6 px-2 text-[10px]" data-testid={`waze-${p.id}`}>
                              <ExternalLink size={10} className="mr-1" /> Waze
                            </Button>
                          </a>
                        </div>
                      </TableCell>
                      <TableCell className="text-xs uppercase">{p.tipo_atendimento}</TableCell>
                      <TableCell><Badge className={bpr.cls + " border"}>{bpr.label}</Badge></TableCell>
                      <TableCell>
                        {!encerrada ? (
                          <Select value={p.status} onValueChange={(v) => atualizarStatusParada(p.id, v)}>
                            <SelectTrigger className="h-7 text-xs" data-testid={`sel-status-parada-${p.id}`}><SelectValue /></SelectTrigger>
                            <SelectContent>
                              {STATUS_PARADA.map((s) => <SelectItem key={s.v} value={s.v}>{s.label}</SelectItem>)}
                            </SelectContent>
                          </Select>
                        ) : <Badge className={bp.cls + " border"}>{bp.label}</Badge>}
                      </TableCell>
                      <TableCell className="text-right">
                        {!encerrada && (
                          <Button size="sm" variant="ghost" onClick={() => removerParada(p.id)} data-testid={`remover-parada-${p.id}`}>
                            <Trash2 size={14} className="text-red-600" />
                          </Button>
                        )}
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </Card>
        </TabsContent>

        {/* INFO */}
        <TabsContent value="info" className="pt-3">
          <Card className="p-4 space-y-2 text-sm">
            <div><strong>Número:</strong> {rota.numero}</div>
            <div><strong>Data:</strong> {rota.data}</div>
            <div><strong>Tipo de operação:</strong> {rota.tipo_operacao}</div>
            <div><strong>Motorista:</strong> {rota.motorista_nome || "—"}</div>
            <div><strong>Veículo:</strong> {rota.veiculo_placa || "—"}</div>
            <div><strong>Observações:</strong> {rota.observacoes || "—"}</div>
            <div><strong>Criada por:</strong> {rota.criado_por_nome} em {(rota.criado_em || "").slice(0, 19).replace("T"," ")}</div>
            <div><strong>Link público motorista:</strong> <code className="text-xs bg-zinc-100 px-2 py-1 rounded">{window.location.origin}/rota/{rota.link_token}</code></div>
          </Card>
        </TabsContent>

        {/* HISTÓRICO */}
        <TabsContent value="historico" className="pt-3">
          {historico.length === 0 ? (
            <p className="text-sm text-muted-foreground text-center py-4">Sem eventos.</p>
          ) : (
            <div className="space-y-2">
              {historico.map((h) => (
                <Card key={h.id} className="p-2 text-xs flex items-center justify-between">
                  <span><strong>{h.acao}</strong> · {h.usuario_email || "público"}</span>
                  <span className="text-muted-foreground">{(h.criado_em || "").slice(0, 19).replace("T"," ")}</span>
                </Card>
              ))}
            </div>
          )}
        </TabsContent>
      </Tabs>

      {/* DIALOG: Nova parada */}
      <Dialog open={openParada} onOpenChange={(o) => { setOpenParada(o); if (!o) setAlertaDup(null); }}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="dialog-parada">
          <DialogHeader>
            <DialogTitle>Nova Parada</DialogTitle>
            <DialogDescription>Busque o cliente para preencher dados automaticamente.</DialogDescription>
          </DialogHeader>

          {/* Busca de cliente */}
          <div className="space-y-2">
            <Label className="text-xs">Buscar cliente (base unificada)</Label>
            <div className="relative">
              <Search size={14} className="absolute left-2.5 top-3 text-muted-foreground" />
              <Input
                className="pl-8"
                placeholder="Nome, CNPJ, e-mail..."
                value={clienteBusca}
                onChange={(e) => setClienteBusca(e.target.value)}
                data-testid="parada-busca-cliente"
              />
            </div>
            {buscando && <p className="text-xs text-muted-foreground"><Loader2 size={10} className="inline animate-spin" /> Buscando...</p>}
            {clienteOpts.length > 0 && (
              <div className="border rounded max-h-40 overflow-y-auto">
                {clienteOpts.map((c) => (
                  <button
                    key={c.id}
                    type="button"
                    onClick={() => selecionarCliente(c)}
                    className="w-full text-left px-3 py-2 text-xs hover:bg-zinc-50 border-b last:border-b-0"
                    data-testid={`opt-cliente-${c.id}`}
                  >
                    <div className="font-medium">{c.nome}</div>
                    <div className="text-muted-foreground">
                      {c.cnpj_cpf} · {c.origem === "locativo" ? "Base" : "CRM"} · {c.cidade || ""} {c.uf || ""}
                    </div>
                  </button>
                ))}
              </div>
            )}
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div className="col-span-2">
              <Label>Cliente *</Label>
              <Input value={paradaForm.cliente_nome} onChange={(e) => setParadaForm({ ...paradaForm, cliente_nome: e.target.value })} data-testid="input-cliente-nome" />
            </div>
            <div className="col-span-2">
              <Label>Endereço *</Label>
              <Input value={paradaForm.endereco} onChange={(e) => setParadaForm({ ...paradaForm, endereco: e.target.value })} data-testid="input-endereco" />
            </div>
            <div>
              <Label>Cidade</Label>
              <Input value={paradaForm.cidade} onChange={(e) => setParadaForm({ ...paradaForm, cidade: e.target.value })} />
            </div>
            <div>
              <Label>UF</Label>
              <Input value={paradaForm.uf} onChange={(e) => setParadaForm({ ...paradaForm, uf: e.target.value.toUpperCase().slice(0, 2) })} />
            </div>
            <div>
              <Label>Telefone</Label>
              <Input value={paradaForm.telefone} onChange={(e) => setParadaForm({ ...paradaForm, telefone: e.target.value })} />
            </div>
            <div>
              <Label>Tipo atendimento</Label>
              <Select value={paradaForm.tipo_atendimento} onValueChange={(v) => setParadaForm({ ...paradaForm, tipo_atendimento: v })}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>{TIPOS_ATENDIMENTO_PARADA.map((t) => <SelectItem key={t.v} value={t.v}>{t.label}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div>
              <Label>Prioridade</Label>
              <Select value={paradaForm.prioridade} onValueChange={(v) => setParadaForm({ ...paradaForm, prioridade: v })}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>{PRIORIDADES.map((p) => <SelectItem key={p.v} value={p.v}>{p.label}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div className="col-span-2">
              <Label className="text-xs">Equipamentos (um por linha)</Label>
              <Textarea
                rows={2}
                value={(paradaForm.equipamentos || []).join("\n")}
                onChange={(e) => setParadaForm({ ...paradaForm, equipamentos: e.target.value.split("\n").filter((x) => x.trim()) })}
                placeholder="Ex: Empilhadeira 2t — Série XPTO"
              />
            </div>
            <div className="col-span-2">
              <Label>Observações</Label>
              <Textarea rows={2} value={paradaForm.observacoes} onChange={(e) => setParadaForm({ ...paradaForm, observacoes: e.target.value })} />
            </div>
          </div>

          {alertaDup && (
            <div className="p-3 bg-amber-50 border border-amber-300 rounded text-sm flex gap-2" data-testid="alerta-endereco-duplicado">
              <AlertTriangle size={16} className="text-amber-700 shrink-0 mt-0.5" />
              <div>
                <strong className="text-amber-900">Endereço duplicado.</strong>
                <p className="text-xs text-amber-800 mt-1">{alertaDup.msg} ({alertaDup.cliente})</p>
                <div className="flex gap-2 mt-2">
                  <Button size="sm" variant="outline" onClick={() => salvarParada(true)} data-testid="btn-agrupar">Agrupar</Button>
                  <Button size="sm" variant="ghost" onClick={() => setAlertaDup(null)}>Manter separado (criar nova parada)</Button>
                </div>
              </div>
            </div>
          )}

          <DialogFooter>
            <Button variant="outline" onClick={() => setOpenParada(false)}>Cancelar</Button>
            <Button onClick={() => salvarParada(false)} disabled={busy} className="text-white" style={{ background: "#1B3A6B" }} data-testid="btn-salvar-parada">
              {busy ? <Loader2 className="animate-spin mr-1" size={14} /> : <Plus size={14} className="mr-1" />}
              Adicionar
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
